/**
 * Burnout showcase — a coverflow carousel.
 *
 * One slide centred and full size, its neighbours pushed back and dimmed on
 * either side. Position is driven entirely from a single integer (the active
 * index): every slide's transform is a function of its distance from it, which
 * is what keeps the motion coherent when several things move at once — a drag,
 * an autoplay tick and a click can all land in the same frame without fighting.
 *
 * Transforms are written from JS rather than from :nth-child CSS because the
 * offsets wrap: with five slides, slide 0 sits to the *right* of slide 4, and
 * no static rule can express that.
 */

class BurnoutShowcase extends HTMLElement {
  #slides = [];
  #active = 0;
  #timer = null;
  #pointer = null;
  #io = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-bo-sc-slide]'));
    if (this.#slides.length === 0) return;

    this.#active = Math.min(Number(this.dataset.start || 0), this.#slides.length - 1);

    this.#bind();
    this.#layout();

    // Autoplay only while the section is actually on screen. A carousel
    // advancing in a scrolled-past section burns battery and, worse, means the
    // visitor returns to a slide they never chose.
    if (this.#interval > 0) {
      this.#io = new IntersectionObserver(
        (entries) => (entries[0]?.isIntersecting ? this.#play() : this.#pause()),
        { threshold: 0.35 }
      );
      this.#io.observe(this);
    }
  }

  disconnectedCallback() {
    this.#pause();
    this.#io?.disconnect();
  }

  get #interval() {
    const value = Number(this.dataset.autoplay || 0);
    return Number.isFinite(value) ? value : 0;
  }

  get #reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Controls are looked up from the section, not from this element.
   *
   * The arrows sit in the section header beside the heading — deliberately, so
   * they match every other Burnout rail — which puts them *outside* this custom
   * element. Querying `this` found nothing and the arrows silently did nothing.
   */
  get #controls() {
    return this.closest('.bo-sc') ?? this;
  }

  #bind() {
    this.#controls.querySelector('[data-bo-sc-prev]')?.addEventListener('click', () => this.#step(-1, true));
    this.#controls.querySelector('[data-bo-sc-next]')?.addEventListener('click', () => this.#step(1, true));

    this.#slides.forEach((slide, index) => {
      // Clicking a side slide brings it in. The centre slide keeps its own link
      // behaviour, so only the off-centre ones swallow the click.
      slide.addEventListener(
        'click',
        (event) => {
          if (index === this.#active) return;
          event.preventDefault();
          this.#goTo(index, true);
        },
        true
      );
    });

    this.querySelectorAll('[data-bo-sc-dot]').forEach((dot, index) => {
      dot.addEventListener('click', () => this.#goTo(index, true));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.#step(-1, true);
      if (event.key === 'ArrowRight') this.#step(1, true);
    });

    this.addEventListener('pointerenter', () => this.#pause());
    this.addEventListener('pointerleave', () => this.#play());
    this.addEventListener('focusin', () => this.#pause());

    this.#bindDrag();
  }

  /**
   * Pointer drag. Only the horizontal intent is captured — the vertical axis is
   * left alone so a swipe down the page still scrolls, which is the thing
   * carousels most often get wrong on a phone.
   */
  #bindDrag() {
    const track = this.querySelector('[data-bo-sc-track]') ?? this;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.#pointer = { x: event.clientX, y: event.clientY, locked: null };
      this.#pause();
    });

    track.addEventListener('pointermove', (event) => {
      if (!this.#pointer) return;
      const dx = event.clientX - this.#pointer.x;
      const dy = event.clientY - this.#pointer.y;

      if (this.#pointer.locked === null) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        this.#pointer.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }

      if (this.#pointer.locked === 'x') event.preventDefault();
    });

    const release = (event) => {
      if (!this.#pointer) return;
      const dx = event.clientX - this.#pointer.x;
      const locked = this.#pointer.locked;
      this.#pointer = null;

      if (locked === 'x' && Math.abs(dx) > 40) this.#step(dx < 0 ? 1 : -1, true);
      this.#play();
    };

    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', () => {
      this.#pointer = null;
      this.#play();
    });
  }

  #step(direction, fromUser) {
    this.#goTo(this.#active + direction, fromUser);
  }

  #goTo(index, fromUser) {
    const count = this.#slides.length;
    this.#active = ((index % count) + count) % count;
    this.#layout();
    if (fromUser) this.#play();
  }

  /**
   * Shortest signed distance from the active index, wrapping both ways.
   * With 5 slides and active = 0, slide 4 is at -1, not +4.
   */
  #offset(index) {
    const count = this.#slides.length;
    let off = index - this.#active;
    if (off > count / 2) off -= count;
    if (off < -count / 2) off += count;
    return off;
  }

  #layout() {
    const depth = Number(this.dataset.depth || 140);
    const spread = Number(this.dataset.spread || 62);
    const shrink = Number(this.dataset.shrink || 16) / 100;
    const tilt = Number(this.dataset.tilt || 7);
    const visible = Number(this.dataset.visible || 2);

    this.#slides.forEach((slide, index) => {
      const off = this.#offset(index);
      const distance = Math.abs(off);
      const beyond = distance > visible;

      slide.style.transform = this.#reduced
        ? 'none'
        : `translate3d(${off * spread}%, 0, ${-distance * depth}px) rotateY(${-off * tilt}deg) scale(${Math.max(
            0.4,
            1 - distance * shrink
          )})`;
      slide.style.opacity = beyond ? '0' : String(Math.max(0, 1 - distance * 0.34));
      slide.style.zIndex = String(100 - distance);
      slide.style.pointerEvents = beyond ? 'none' : 'auto';
      slide.toggleAttribute('data-bo-sc-active', off === 0);
      slide.setAttribute('aria-hidden', off === 0 ? 'false' : 'true');

      // A link two slides deep must not be reachable by tab.
      const link = slide.querySelector('a');
      if (link) link.tabIndex = off === 0 ? 0 : -1;
    });

    this.querySelectorAll('[data-bo-sc-dot]').forEach((dot, index) => {
      const current = index === this.#active;
      dot.toggleAttribute('data-bo-sc-current', current);
      dot.setAttribute('aria-current', current ? 'true' : 'false');
    });
  }

  #play() {
    this.#pause();
    if (this.#interval <= 0 || this.#reduced || this.#slides.length < 2) return;
    this.#timer = setInterval(() => this.#step(1, false), this.#interval);
  }

  #pause() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }
}

if (!customElements.get('burnout-showcase')) {
  customElements.define('burnout-showcase', BurnoutShowcase);
}
